import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import { BusinessSubscriptionModel, billingApi } from '@pukka/api';
import { hookUtils, animationUtil } from '@pukka/common/utils';
import { storage } from '@pukka/storage';
import { InformationBox, Text } from '@pukka/ui-components';
import { useMutation } from '@tanstack/react-query';
import { format, isAfter, isSameDay, subMonths } from 'date-fns';

import BillingDiscountModal from './BillingDiscountModal';
import { shouldShowPopup } from './utils';

interface BillingDiscountReminderProps {
  readonly show?: boolean;
  readonly businessId?: number;
  readonly expiredAt?: string;
  readonly isBanner?: boolean;
  readonly onClose?: () => void;
}

interface BillingDiscountReminderContentProps {
  readonly expiredDate: string;
  readonly endDate: string;
  readonly discount?: number | string;
}

function getStorageKey(businessId?: number) {
  return `lastSeenBillingDiscountPopup:${businessId}`;
}

function BillingDiscountReminderContent({ expiredDate, endDate, discount = '0' }: BillingDiscountReminderContentProps) {
  return (
    <>
      Langganan Anda akan berakhir {expiredDate}.{' '}
      <strong>
        Segera perbarui paling lambat {endDate} untuk dapatkan diskon {discount}%
      </strong>{' '}
      biaya berlangganan.{' '}
    </>
  );
}

function timeoutClear(timeout?: number) {
  if (timeout) {
    clearTimeout(timeout);
  }
}

function BillingDiscountReminder({ businessId, expiredAt, isBanner, show, onClose }: BillingDiscountReminderProps) {
  const isMobile = hookUtils.useIsMobile();
  const [discountSettings, setDiscountSettings] = useState<BusinessSubscriptionModel.DiscountSettings>();
  const { discount_schemes: discountSchemes } = discountSettings || {};
  const hasDiscountScheme = (discountSchemes?.length ?? 0) > 0;
  const [showModal, setShowModal] = useState(false);
  const [showReminder, setShowReminder] = useState(!!show && hasDiscountScheme);
  const { mutate: fetchDiscountSettings } = useMutation(billingApi.getDiscountSettings, {
    onSuccess({ data }) {
      setDiscountSettings(data);
    },
  });

  const formattedExpiredDate = expiredAt ? format(new Date(expiredAt), 'dd MMM yyyy') : '';
  const discountObj = discountSchemes?.find((disc) => !!disc.selected);
  const formattedEndDate = format(discountObj?.end_date ? new Date(discountObj?.end_date) : new Date(), 'dd MMM yyyy');

  const handleShowModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);

    storage.set(getStorageKey(businessId), format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx"));
  }, [businessId]);

  const handleToggleModal = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        handleShowModal();
      } else {
        handleCloseModal();
      }
    },
    [handleCloseModal, handleShowModal],
  );

  useEffect(() => {
    if (!businessId) {
      return;
    }

    let timeout: number;

    if (discountSettings) {
      if (isBanner) {
        const lastSeen = storage.get(getStorageKey(businessId));

        if (shouldShowPopup(discountSettings, new Date(), lastSeen)) {
          handleShowModal();
        }
      } else if (!isBanner && window.location.hash === '#fromBanner') {
        timeout = window.setTimeout(handleShowModal, 1000);
      }
    } else {
      // only owner can see billing discount scheme
      const today = new Date();
      const expiredDate = expiredAt ? new Date(expiredAt) : new Date();
      const minusSixMonth = subMonths(expiredDate, 6);
      const isSameDayOrAfterM6 = isSameDay(today, minusSixMonth) || isAfter(today, minusSixMonth);

      if (isSameDayOrAfterM6) {
        fetchDiscountSettings(businessId);
      }
    }

    // eslint-disable-next-line consistent-return
    return () => timeoutClear(timeout);
  }, [businessId, discountSettings, expiredAt, isBanner, fetchDiscountSettings, handleShowModal]);

  useLayoutEffect(() => {
    const shouldShow = !!show && hasDiscountScheme;

    if (shouldShow !== showReminder) {
      animationUtil.doTransition(() => {
        flushSync(() => setShowReminder(shouldShow));
      });
    }
  }, [show, hasDiscountScheme, showReminder]);

  return showReminder ? (
    <>
      {isBanner ? (
        <div role="alert" data-testid="billing-discount-reminder" className="z-20 bg-yellow-100 py-3 pl-4 pr-8 text-xs md:px-14">
          <button
            type="button"
            data-testid="close-billing-discount-banner"
            onClick={onClose}
            className="absolute right-4 top-1 border-none bg-inherit p-0 text-lg md:right-12"
          >
            &times;
          </button>
          <BillingDiscountReminderContent expiredDate={formattedExpiredDate} endDate={formattedEndDate} discount={discountObj?.discount} />
          <Text as="a" weight="bold" href="/v2/settings/billing#fromBanner" className="border-none bg-inherit p-0 underline">
            Info selengkapnya
          </Text>
        </div>
      ) : (
        <InformationBox variant="warning" className="view-transition-[billing-reminder]">
          <BillingDiscountReminderContent expiredDate={formattedExpiredDate} endDate={formattedEndDate} discount={discountObj?.discount} />
          <button type="button" className="font-semibold underline" onClick={handleShowModal}>
            Info selengkapnya
          </button>
        </InformationBox>
      )}

      {discountSchemes && (
        <BillingDiscountModal
          show={showModal}
          fromBanner={isBanner}
          discountSchemes={discountSchemes}
          isMobile={isMobile}
          onClose={handleCloseModal}
          onToggleModal={handleToggleModal}
        />
      )}
    </>
  ) : null;
}

export { BillingDiscountReminder, BillingDiscountReminderContent, timeoutClear as _timeoutClear };
